/**
 * Blog article: print preview via a temporary iframe (article template only).
 */

function findArticlePrintRoot() {
  return (
    document.querySelector('.article-body .article-content-container-for-print') ||
    document.querySelector('.article-content-container-for-print')
  );
}

/** Styles needed for recipe typography — exclude global.css (body::after overlay breaks print). */
const PRINT_STYLE_HREF_PARTS = ['colors.css', 'template-blog.css'];

function collectPrintStylesHtml() {
  const parts = [];
  document.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
    const href = link.getAttribute('href');
    if (!href) return;
    if (PRINT_STYLE_HREF_PARTS.some((chunk) => href.includes(chunk))) {
      parts.push(`<link rel="stylesheet" href="${href}">`);
    }
  });
  return parts.join('');
}

/**
 * @param {ParentNode} root
 */
function prepareHiddenA11yTextForPrint(root) {
  root
    .querySelectorAll(
      '.visually-hidden, .visually-hidden--inline, .visually-hidden-skip, .sr-only, .notify-txt'
    )
    .forEach((el) => el.remove());
}

/**
 * Recipe print: replace all anchors with spans so only visible text/content prints (no URLs).
 * @param {ParentNode} root
 */
function prepareLinksForPrint(root) {
  root.querySelectorAll('a[href]').forEach((link) => {
    const span = document.createElement('span');
    span.className = link.className;
    span.innerHTML = link.innerHTML;
    link.replaceWith(span);
  });
}

/**
 * Lazy / responsive images often break in a 0×0 iframe. Normalize to eager, real URLs.
 * @param {ParentNode} root
 */
function prepareImagesForPrint(root) {
  root.querySelectorAll('picture').forEach((picture) => {
    const fallback = picture.getAttribute('data-iesrc');
    const img = picture.querySelector('img');
    if (!img) return;
    if (fallback) {
      img.src = fallback;
      img.removeAttribute('srcset');
    }
    picture.querySelectorAll('source').forEach((source) => source.remove());
    img.loading = 'eager';
    img.classList.remove('lozad');
  });

  root.querySelectorAll('img').forEach((img) => {
    img.loading = 'eager';
    const dataSrc = img.getAttribute('data-src');
    if (
      dataSrc &&
      (!img.getAttribute('src') ||
        img.src.includes('animated-placeholder') ||
        img.getAttribute('src')?.includes('animated-placeholder.svg'))
    ) {
      img.src = dataSrc;
    }
  });
}

/**
 * @param {Document} doc
 * @returns {Promise<void>}
 */
function waitForImages(doc) {
  const images = Array.from(doc.images || []);
  return Promise.all(
    images.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete && img.naturalHeight !== 0) {
            resolve();
            return;
          }
          img.addEventListener('load', () => resolve(), { once: true });
          img.addEventListener('error', () => resolve(), { once: true });
        })
    )
  ).then(() => undefined);
}

/**
 * @param {Document} doc
 * @returns {Promise<void>}
 */
function waitForStylesheets(doc) {
  const links = Array.from(doc.querySelectorAll('link[rel="stylesheet"]'));
  if (!links.length) return Promise.resolve();
  return Promise.all(
    links.map(
      (link) =>
        new Promise((resolve) => {
          if (link.sheet) {
            resolve();
            return;
          }
          link.addEventListener('load', () => resolve(), { once: true });
          link.addEventListener('error', () => resolve(), { once: true });
          setTimeout(resolve, 2500);
        })
    )
  ).then(() => undefined);
}

class ArticlePrintFrame extends HTMLElement {
  connectedCallback() {
    if (!document.body.classList.contains('template-article')) return;
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this._onDocumentClick = this._onDocumentClick.bind(this);
    document.addEventListener('click', this._onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocumentClick);
  }

  /**
   * @param {MouseEvent} e
   */
  _onDocumentClick(e) {
    const trigger = e.target.closest('[data-print-article]');
    if (!trigger || !trigger.closest('section[role="article"]')) return;
    e.preventDefault();
    this.printArticle();
  }

  printArticle() {
    const printRoot = findArticlePrintRoot();
    if (!printRoot) return;

    const titleFromDom =
      printRoot.querySelector('h1')?.textContent?.trim() ||
      this.dataset.printTitle ||
      document.title;

    const clone = printRoot.cloneNode(true);
    prepareHiddenA11yTextForPrint(clone);
    prepareImagesForPrint(clone);
    prepareLinksForPrint(clone);

    const printCss = `
      <style>
        body::before, body::after { display: none !important; content: none !important; }
        body { margin: 0; padding: 1rem; color: #000; background: #fff; }
        img { max-width: 100% !important; height: auto !important; page-break-inside: avoid; }
        .article-images img { object-fit: contain !important; }
        .article-content-container-for-print, .article-content, .article-content-container-for-print * {
          visibility: visible !important;
          opacity: 1 !important;
        }
        .visually-hidden, .visually-hidden--inline, .visually-hidden-skip, .sr-only, .notify-txt {
          display: none !important;
        }
        @media print {
          body { padding: 0; }
          img { max-width: 100% !important; height: auto !important; }
        }
      </style>`;

    const html = [
      '<!DOCTYPE html><html><head><meta charset="utf-8"><title>',
      escapeHtml(titleFromDom),
      '</title>',
      collectPrintStylesHtml(),
      printCss,
      '</head><body>',
      clone.outerHTML,
      '</body></html>',
    ].join('');

    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.setAttribute('tabindex', '-1');
    iframe.style.cssText =
      'position:fixed;left:-10000px;top:0;width:1200px;height:2400px;border:0;opacity:0;pointer-events:none;';
    this.appendChild(iframe);

    const win = iframe.contentWindow;
    if (!win) {
      iframe.remove();
      return;
    }

    const idoc = win.document;
    idoc.open();
    idoc.write(html);
    idoc.close();

    prepareImagesForPrint(idoc.body);

    const removeFrame = () => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    };

    const runPrint = () => {
      try {
        win.focus();
        win.print();
      } catch (_err) {
        /* ignore */
      }
    };

    win.addEventListener('afterprint', removeFrame, { once: true });
    setTimeout(removeFrame, 120000);

    Promise.all([waitForImages(idoc), waitForStylesheets(idoc)])
      .then(() => {
        if (typeof win.requestAnimationFrame === 'function') {
          win.requestAnimationFrame(() => {
            win.requestAnimationFrame(() => runPrint());
          });
        } else {
          setTimeout(runPrint, 100);
        }
      })
      .catch(() => setTimeout(runPrint, 400));
  }
}

/**
 * @param {string} text
 * @returns {string}
 */
function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

if (!customElements.get('print-frame')) {
  customElements.define('print-frame', ArticlePrintFrame);
}
